import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";

import { buildSidebarCommunities } from "../../../../helpers/feedSidebar";
import styles from "./followingSidebar.module.css";

/**
 * The My Feed community sidebar (GROWTH-7130): "All communities" plus one row per community the
 * member actually follows, replacing the old plain-link list. Purely presentational — the Feed
 * page owns `communityId` and the data this reads.
 *
 * @param {Object} props
 * @param {{all: boolean, heritage_ids: number[]}} props.communities state.following.scope.communities
 * @param {Map<number, string>} props.titles heritage_id -> title
 * @param {Object<string, number>} props.newByCommunity GET /api-v2/feed's new_by_community
 * @param {number} props.newCount whole-feed new count, for the "All communities" row
 * @param {{all: ?string, communities: Array<{heritage_id: number, url: string}>}} [props.visitUrls]
 *   state.following.scope.visit_urls — each row's public-site link (GROWTH-7216); a row without one has no link
 * @param {number|null} props.communityId the active selection; null = "All communities"
 * @param {Function} props.onSelect (id: number|null) => void
 */
const FollowingSidebar = ({ communities, titles, newByCommunity, newCount, visitUrls, communityId, onSelect }) => {
  const { t } = useTranslation();
  const rows = useMemo(() => buildSidebarCommunities(communities, titles, newByCommunity), [communities, titles, newByCommunity]);
  const urlById = useMemo(() => new Map((visitUrls?.communities || []).map(item => [item.heritage_id, item.url])), [visitUrls]);

  // GROWTH-7217: a zero count has no label at all; only news is worth a status.
  const countLabel = count => (count > 0 ? t("dashboard_feed_new_count", { count }) : null);

  // The select button and the link are siblings: a link can't sit inside a button, and a click on
  // the link must not change the selection.
  const renderRow = ({ key, title, label, isActive, onClick, url }) => (
    <div key={key} className={cn(styles.item, { [styles.itemActive]: isActive })}>
      <button type="button" className={cn(styles.row, { [styles.rowActive]: isActive })} aria-pressed={isActive} onClick={onClick}>
        <span className={styles.rowName}>{title}</span>
        {label ? <span className={styles.rowCount}>{label}</span> : null}
      </button>
      {url ? (
        <a
          className={styles.visit}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t("dashboard_feed_sidebar_visit_site_aria", { community: title })}
        >
          {t("dashboard_feed_sidebar_visit_site")}
        </a>
      ) : null}
    </div>
  );

  return (
    <div className={styles.list} role="group" aria-label={t("dashboard_feed_sidebar_group_label")}>
      {renderRow({
        key: "all",
        title: t("dashboard_feed_sidebar_all_communities"),
        label: countLabel(newCount),
        isActive: communityId === null,
        onClick: () => onSelect(null),
        url: visitUrls?.all
      })}

      {rows.map(row =>
        renderRow({
          key: row.id,
          title: row.title,
          label: countLabel(row.count),
          isActive: communityId === row.id,
          onClick: () => onSelect(row.id),
          url: urlById.get(row.id)
        })
      )}
    </div>
  );
};

export default FollowingSidebar;
